"use client";

import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import { TwinMark } from "@product/components/app/twin/twin-mark";
import { ConnectedAnswer } from "./connected-answer";
import { StoryParticles } from "./story-particles";
import { NORTHSTAR_REVIEW } from "./northstar-review";
import { CHAPTERS, SOURCES } from "./story-sources";
import { PERSONA_STORIES, type StoryVariant } from "./persona-stories";
import styles from "./twin-story.module.css";

const ReviewExhibit = dynamic(() => import("@/components/editorial/sectors/review-exhibit").then(module => module.ReviewExhibit));

// Art-directed scatter: stable on refresh, with gaps between neighbouring badges.
const SOURCE_POSITIONS = [
  [15, 16, -3], [35, 23, 2], [57, 12, -2], [78, 21, 3],
  [10, 38, 1], [29, 43, -2], [51, 34, 2], [72, 39, -3], [91, 32, 1],
  [18, 59, -2], [42, 55, 3], [62, 54, -1], [84, 57, 2],
  [8, 76, 2], [29, 70, -3], [51, 76, 1], [73, 72, -2], [93, 78, 3],
  [20, 90, -1], [42, 92, 2], [64, 89, -3], [84, 93, 1],
  [91, 11, -2], [10, 5, 2],
];

const CUSTOMER_SOURCES: Record<number, { label: string; x: number; y: number }> = {
  1: { label: "Northstar · Customer", x: 19, y: 26 },
  2: { label: "Payment expected Friday", x: 78, y: 73 },
  3: { label: "INV-2048 · £8,400", x: 79, y: 28 },
  4: { label: "Website · Delivered", x: 22, y: 75 },
};

export function ScrollStory({ compact = false, landing = false, variant = "business", workspace }: { compact?: boolean; landing?: boolean; variant?: StoryVariant; workspace?: ReactNode }) {
  const persona = variant === "business" ? undefined : PERSONA_STORIES[variant];
  const chapters = persona?.chapters ?? CHAPTERS;
  const sources = persona?.sources ?? SOURCES;
  const customerSources = persona?.focus ?? CUSTOMER_SOURCES;
  const root = useRef<HTMLElement>(null);
  const [chapter, setChapter] = useState(0);
  const [manual, setManual] = useState(compact);
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 760px)");
    const update = () => setMobile(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  useEffect(() => {
    if (manual || mobile) return;
    const element = root.current;
    if (!element) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const box = element.getBoundingClientRect();
      const top = landing ? 68 : 0;
      const distance = Math.max(1, box.height - window.innerHeight + top);
      const progress = Math.max(0, Math.min(.999, (top - box.top) / distance));
      setChapter(Math.floor(progress * chapters.length));
    };
    const scroll = () => { if (!frame) frame = requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", scroll, { passive: true });
    window.addEventListener("resize", scroll);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", scroll);
      window.removeEventListener("resize", scroll);
    };
  }, [manual, mobile, landing, chapters.length]);
  const current = chapters[chapter]!;
  const following = chapter === 1;
  const showSources = chapter < 2;
  return <section ref={root} id={persona ? "workflow" : landing ? "platform" : undefined} className={`${styles.story} ${compact ? styles.compactStory : ""} ${landing ? styles.landingStory : ""}`} aria-label="From sources to an answer">
    <div className={styles.sticky}>
      <div className={styles.storyHead}>{!landing ? <p className={styles.kicker}>01 / WHY A TWIN?</p> : null}
        <span className={styles.hint}>{compact || manual || mobile ? "Choose a chapter below" : "Scroll to follow the evidence ↓"}</span></div>
      <div className={styles.storyCopy}><h2>{current.title}</h2><p>{current.body}</p></div>
      <div className={styles.storyArt} data-chapter={chapter}>
        <StoryParticles stage={chapter < 2 ? 0 : mobile && chapter === 3 ? 1 : Math.min(2, chapter - 1)} label={current.name} />
        <div className={styles.sourceLabels} aria-hidden={!showSources} data-visible={showSources} data-following={following}>
          <svg className={styles.customerConnections} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            {Object.entries(customerSources).map(([id, point]) => <path key={id} d={`M${point.x} ${point.y} Q50 ${point.y} 50 50`} />)}
          </svg>
          <div className={styles.customerFocus} aria-hidden={!following}><TwinMark /><strong>{persona?.entity ?? "Northstar"}</strong><span>Illustrative relationship</span></div>
          {sources.map(({ label, icon, source, mobile }, i) => <span key={label} data-mobile={!!mobile} data-related={!!customerSources[i]} style={{
            "--source-x": `${following && customerSources[i] ? customerSources[i].x : SOURCE_POSITIONS[i]![0]}%`,
            "--source-y": `${following && customerSources[i] ? customerSources[i].y : SOURCE_POSITIONS[i]![1]}%`,
            "--source-rotation": `${SOURCE_POSITIONS[i]![2]}deg`,
          } as CSSProperties}>
            <span className={styles.sourceIcon}><Image src={`/concepts/source-icons/${icon}`} width={14} height={14} alt={source} /></span>{following && customerSources[i] ? customerSources[i].label : label}
          </span>)}
        </div>
        <div className={styles.connectedCards} data-visible={chapter === 3} inert={chapter !== 3} aria-hidden={chapter !== 3}>
          <ConnectedAnswer active={chapter === 3} answer={persona?.answer} />
        </div>
        <div className={styles.workspaceStage} data-visible={chapter === 4} inert={chapter !== 4} aria-hidden={chapter !== 4}>
          {chapter >= 3 ? workspace ?? <ReviewExhibit review={NORTHSTAR_REVIEW} story /> : null}
        </div>
      </div>
      <div className={styles.chapterNav} aria-label="Story chapters">
        {chapters.map((item, i) => <button key={item.name} aria-pressed={chapter === i} onClick={() => { setManual(true); setChapter(i); }}><span>0{i + 1}</span>{item.name}<span className={styles.chapterRule} /></button>)}
        {!compact && manual && !mobile ? <button className={styles.scrollReset} onClick={() => setManual(false)}>Follow scroll ↕</button> : null}
      </div>
    </div>
  </section>;
}
